import * as Popover from '@radix-ui/react-popover'
import { ArrowUpRight, Edit } from '@/assets/icons'
import { useState, useRef, useContext, useCallback, useEffect } from 'react'
import { PopoverContainerContext } from '@/context/popover-container-context'
import { MedSummaryReference } from '../store/schemas'
import { useAppSelector } from '@/store/store-hooks'
import type { CellPosition, ScrollToCell } from '@/medical-common/hooks/use-table-cell-scroll'
import { useDebouncedEffect } from '@/hooks/useDebouncedEffect'
import { useIsTouchOnlyDevice } from '@/hooks/useIsTouchOnlyDevice'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { useMedSummaryParent } from '@/medical-billing-summaries/parent-context'
import { selectAssetFileByUserFileId } from '@/medical-billing-summaries/asset/store/selectors'
import { useOpenMedSummarySource } from '@/medical-billing-summaries/sources/useOpenMedSummarySource'
import { RootState } from '@/store/store'
import clsx from 'clsx'

interface ViewSourceEditPopoverProps {
  reference: MedSummaryReference | null
  cellPosition: CellPosition
  scrollToCell: ScrollToCell
  isCellHovered?: boolean
  onItemHighlightChange?: (isHighlighted: boolean) => void // allows wrapped element to update its highlight state, synced with popover visibility
  onStartEdit: () => void // function to enter edit mode
  children: React.ReactElement
  alignOffsetDelta?: number // Additive nudge on top of the component's base `alignOffset`, using a delta so base alignment updates don't require updates to call sites
}

/**
 * View Source Edit Popover
 *
 * Reusable wrapper that adds "View source/Edit" hover/tap behavior to any referenced element.
 * It separates popover logic from rendering, allowing any content to be wrapped
 * with consistent "View source/Edit" hover/tap behavior.
 *
 * Can be opened from:
 * - Item-level hover/tap (when the wrapped element itself is hovered/tapped), or
 * - Cell-level hover (desktop only)
 *
 * Used in table cells to open the source viewer for a reference, or enter edit mode for a field.
 */
export default function ViewSourceEditPopover(props: ViewSourceEditPopoverProps) {
  const { reference, cellPosition, scrollToCell, isCellHovered = false, onItemHighlightChange, onStartEdit, children, alignOffsetDelta } = props
  const { trackEvent } = useAnalytics()
  const { id: parentId, readOnly } = useMedSummaryParent()
  const isTouchOnlyDevice = useIsTouchOnlyDevice()
  const { openMedSummarySource, sourceLoadError } = useOpenMedSummarySource()

  // Non-PDF sources were extracted against a converted PDF (AI-94); the viewer must open the
  // conversion so citation highlight geometry lines up.
  const hasConvertedPdf = useAppSelector((state: RootState) =>
    reference ? selectAssetFileByUserFileId(state, { assetId: parentId, userFileId: reference.source.source_id })?.converted_pdf_path != null : false
  )

  // Base popover alignOffset - column-specific adjustments are applied via alignOffsetDelta
  const BASE_ALIGN_OFFSET = 12
  const alignOffset = BASE_ALIGN_OFFSET + (alignOffsetDelta ?? 0)

  // Check if any field is being edited (disables view source interactions)
  const isAnyFieldEditing = useAppSelector((state) => state.medSummaryEntryEditState.activeEdit !== null)

  // Local UI state
  const [hasInteracted, setHasInteracted] = useState(false)
  const [showPopover, setShowPopover] = useState(false)
  const [isItemHovered, setIsItemHovered] = useState(false)

  // Refs
  const containerEl = useContext(PopoverContainerContext)
  const triggerRef = useRef<HTMLElement | null>(null)
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const wasEditingOnPointerDownRef = useRef(false)

  // Derived states
  const isHovered = isCellHovered || isItemHovered
  const shouldShowPopover = isHovered && !isAnyFieldEditing

  // Toggle popover and highlight visibility (the two should always be in sync)
  const togglePopoverWithHighlight = useCallback(
    (open: boolean) => {
      onItemHighlightChange?.(open)
      setShowPopover(open)
    },
    [onItemHighlightChange]
  )

  const baseButtonStyles = 'flex items-center gap-1 text-xs leading-4 text-brand-neutral-800 font-medium focus:outline-hidden'

  // Desktop: Debounce popover and highlight visibility to prevent flickering when cursor briefly passes over trigger
  useDebouncedEffect(() => {
    if (isTouchOnlyDevice) return

    togglePopoverWithHighlight(shouldShowPopover)
  }, [shouldShowPopover, onItemHighlightChange, isTouchOnlyDevice])

  const handleViewSource = async () => {
    if (!reference) return

    scrollToCell(cellPosition)
    const opened = await openMedSummarySource(reference.source, {
      reference,
      highlights: reference.highlights,
      startingPage: reference.highlights.length === 0 ? reference.pages?.[0] : undefined,
      convertedPdf: hasConvertedPdf ? { assetId: parentId, userFileId: reference.source.source_id } : undefined,
    })

    if (!opened) {
      setHasInteracted(true)
      togglePopoverWithHighlight(true)
      return
    }

    togglePopoverWithHighlight(false)

    trackEvent(AnalyticsEvent.MedSummaryAssetReferenceViewerOpened, {
      medSummaryAssetId: parentId,
      medSummaryReferenceId: reference.id,
    })
  }

  // Desktop: Single click to view source
  const handleDesktopSingleClick = () => {
    if (isAnyFieldEditing || wasEditingOnPointerDownRef.current) return

    // Delay single-click to distinguish from double-click
    if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current)

    clickTimeoutRef.current = setTimeout(() => {
      handleViewSource()
      clickTimeoutRef.current = null
    }, 250)
  }

  // Desktop: Double click to start edit
  const handleDesktopDoubleClick = () => {
    // Cancel pending single-click so we don't open content viewer
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current)
      clickTimeoutRef.current = null
    }
    onStartEdit()
  }

  /**
   * Defer rendering Popover.Root until needed to reduce performance overhead (there are ~4 popovers rendered per row)
   * We always render the trigger, but only render the full Popover wrapper after first interaction
   */
  useEffect(() => {
    if (showPopover && !hasInteracted) {
      setHasInteracted(true)
    }
  }, [showPopover, hasInteracted])

  // Trigger element for touch devices: Direct popover visibility control via tap
  const touchTrigger = (
    <span
      ref={(node) => {
        triggerRef.current = node as HTMLElement | null
      }}
      onClick={() => togglePopoverWithHighlight(true)}
      onDoubleClick={onStartEdit}
      className="block w-full pointer-events-auto"
    >
      {children}
    </span>
  )

  // Trigger element for desktop: Popover visibility controlled by hover via mouse events
  const desktopTrigger = (
    <span
      ref={(node) => {
        triggerRef.current = node as HTMLElement | null
      }}
      onMouseEnter={() => setIsItemHovered(true)}
      onMouseLeave={() => setIsItemHovered(false)}
      onPointerDown={() => {
        wasEditingOnPointerDownRef.current = isAnyFieldEditing
      }}
      onClick={handleDesktopSingleClick}
      onDoubleClick={handleDesktopDoubleClick}
      className="block w-full pointer-events-none"
    >
      {children}
    </span>
  )

  const trigger = isTouchOnlyDevice ? touchTrigger : desktopTrigger

  const referenceLoadError = reference && sourceLoadError?.sourceId === reference.source.source_id ? sourceLoadError.message : null

  // Read-only viewers have no edit affordance; when there's also no reference to view,
  // there's nothing to show in the popover — render children directly.
  if (readOnly && !reference) return children

  // Before user interaction: Render only trigger element (without Popover wrapper)
  if (!hasInteracted) {
    return trigger
  }

  // After first interaction: Render full Popover (trigger + content)
  return (
    <Popover.Root
      open={showPopover}
      onOpenChange={(open) => {
        togglePopoverWithHighlight(open)
      }}
    >
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>

      {/* Pin the portal inside the container when available */}
      <Popover.Portal container={containerEl || undefined}>
        <Popover.Content
          className={clsx(
            'rounded bg-white ring-1 ring-inset ring-brand-neutral-300 z-50',
            reference && !readOnly ? 'px-1' : 'group hover:bg-brand-neutral-100'
          )}
          side="top"
          align="end"
          sideOffset={3}
          alignOffset={alignOffset}
          avoidCollisions
          collisionBoundary={containerEl || undefined}
          collisionPadding={8}
          {...(!isTouchOnlyDevice && {
            onMouseEnter: () => setIsItemHovered(true),
            onMouseLeave: () => setIsItemHovered(false),
          })}
          // Prevent outside interactions from closing the popover if cell hover is active for the item
          onPointerDownOutside={(e) => {
            if (isCellHovered && !isTouchOnlyDevice) e.preventDefault()
          }}
          onFocusOutside={(e) => {
            if (isCellHovered && !isTouchOnlyDevice) e.preventDefault()
          }}
        >
          {reference ? (
            // Has reference: Show "View source" (+ "Edit" on editable surfaces) with divider
            <div>
              <div className="flex items-center gap-[3px]">
                {/* View Source Button */}
                <button
                  onClick={handleViewSource}
                  className={clsx(
                    baseButtonStyles,
                    readOnly
                      ? 'group-hover:text-brand-neutral-950 py-1 px-2'
                      : 'hover:text-brand-neutral-950 hover:bg-brand-neutral-200 pl-1.5 pr-1 py-0.5 rounded-xs'
                  )}
                >
                  View source
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </button>

                {/* Divider + Edit Button — owner only */}
                {!readOnly && (
                  <>
                    <div className="h-7 w-px bg-brand-neutral-300" />
                    <button
                      onClick={() => onStartEdit()}
                      className={clsx(baseButtonStyles, 'hover:text-brand-neutral-950 hover:bg-brand-neutral-200 pl-1.5 pr-1 py-0.5 rounded-xs')}
                    >
                      Edit
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
              {referenceLoadError && <p className="px-1.5 pb-1 text-xs font-medium text-red-800">{referenceLoadError}</p>}
            </div>
          ) : (
            // No reference: Show only "Edit" button
            <button onClick={() => onStartEdit()} className={clsx(baseButtonStyles, 'group-hover:text-brand-neutral-950 py-1 px-2')}>
              Edit
              <Edit className="w-3.5 h-3.5" />
            </button>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
